import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import styles from './tagBulkAddCard.module.css';
import { TagBulk } from '../../services/http/tagBulk.api';
import TagFormat from '../../utils/tagFormat';
import mainStyles from '../main.module.css';
import { DropDownMenuItem } from '../../components/DropdownMenu';
import BaseTagInput from '../../components/BaseTagInput';
import { DuplicatedTag } from '../../services/http/tag.api';
import { ERROR_MESSAGES } from '../../utils/messages';
import SharedAutoComplete from '../../components/SharedAutoComplete/SharedAutoComplete';

interface Props {
  allTags: TagBulk[],
  tag: TagBulk,
  onChangeTagValue: (value: string, isValid: boolean) => void,
  onDeleteTag: () => void,
  onSelectTag: (tag: TagBulk | null) => void,
  onInputFocus?: () => void,
  duplicatedTags?: DuplicatedTag[],
}

export default function TagBulkAddCard({
  allTags,
  tag,
  onChangeTagValue,
  onDeleteTag,
  onSelectTag,
  onInputFocus = () => ({}),
  duplicatedTags = [],
}: Props) {
  const [selectedTag, setSelectedTag] = useState<TagBulk | null>(null);
  // const [isTagValid, setIsTagValid] = useState<boolean>(true);
  const isTagValid = useRef<boolean>(true);

  const isDuplicated = useMemo(
    () => {
      if (!TagFormat.validate(tag.format, tag.formatCheck, tag.value)) return false;

      const normalizedValue = TagFormat.normalizeTagValue(tag.format, tag.value);
      return duplicatedTags.some((dup) => dup.tagLabel === tag.tagLabel
        && dup.tagValues.includes(normalizedValue));
    },
    [duplicatedTags, tag.tagLabel, tag.value, tag.format, tag.formatCheck],
  );

  const currentOptions: DropDownMenuItem<TagBulk>[] = useMemo(() => {
    const tagOptions: DropDownMenuItem<TagBulk>[] = allTags.map((_tag) => ({ text: _tag.tagLabel, value: _tag }));
    if (!selectedTag) return tagOptions;
    const selectedTagOption: DropDownMenuItem<TagBulk> = { text: selectedTag.tagLabel, value: selectedTag };
    const tagOptionsWithSelectedTag = [selectedTagOption, ...tagOptions];
    return tagOptionsWithSelectedTag;
  }, [allTags, selectedTag]);

  const handleTagValueChange = useCallback((value: string, isValid: boolean) => {
    onChangeTagValue(value, isValid);
    isTagValid.current = isValid;
  }, [onChangeTagValue]);

  const handleSelectTag = useCallback((val: TagBulk | null) => {
    setSelectedTag(val);
    onSelectTag(val);
    isTagValid.current = true;
  }, [onSelectTag]);

  const TagFormatTextMap = TagFormat.getTextMap();

  return (
    <div className={styles.sideCardItem}>
      <div className={styles.sideCardItemRow}>
        <div className={[styles.flexCol, styles.autoCompleteBox].join(' ')}>
          <label>変更対象タグ</label>
          <SharedAutoComplete
            options={[{ text: '', value: null }, ...currentOptions]}
            onSelect={({ value }) => handleSelectTag(value)}
            onDeselect={() => handleSelectTag(null)}
            value={selectedTag ? selectedTag.tagLabel : ''}
          />

        </div>
        <div className={styles.formatCol}>
          {selectedTag ? (
            <>
              <div className={styles.formatLabel}>書式</div>
              <div className={styles.formatText}>{TagFormatTextMap[selectedTag.format]}</div>
            </>
          ) : null}
        </div>
        <div>
          <img className={[styles.deleteCardButton].join(' ')} src="/images/Close-333333.svg" alt="" onClick={() => onDeleteTag()} />
        </div>
      </div>

      <div className={styles.sideCardItemRow}>
        <div className={styles.inputCol}>
          <label>
            変更後の値
            {selectedTag && selectedTag.required ? (
              <span className={mainStyles['text-danger']} style={{ marginLeft: '10px' }}>※</span>
            ) : null}
          </label>
          <BaseTagInput
            tag={tag}
            className={`${styles.tagInput} ${isDuplicated || !isTagValid.current ? mainStyles.invalidBorder : ''}`}
            containerClassName={styles.tagInputContainer}
            onFocus={onInputFocus}
            onChange={handleTagValueChange}
            onBlur={() => {}}
          />
          <div className={styles.errorContainer}>
            {!isTagValid.current && !isDuplicated ? (
              <span>
                {TagFormat.getInvalidErrorMessage(tag.format, tag.formatCheck, tag.value)}
              </span>
            ) : null}
            {isDuplicated ? (
              <span className={mainStyles['text-danger']}>
                {ERROR_MESSAGES.DUPLICATE_TAG_ERROR}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
